/**
 * Charts Component
 * Displays cache hit/miss pie chart, latency histogram, and cost trends
 * Redesigned for clarity and non-technical accessibility
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useDashboardStore } from '../store/dashboardStore';
import { TrendingUp, Info } from 'lucide-react';

// Custom Tooltip for Line Chart
const LineTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/90 border border-white/10 rounded-xl shadow-2xl px-3 py-2 text-xs backdrop-blur-md">
        <p className="font-bold text-slate-200">{label}:00</p>
        <p className="text-emerald-400 font-semibold">${(payload[0].value as number).toFixed(3)} saved this hour</p>
      </div>
    );
  }
  return null;
};

// Custom Tooltip for Cumulative Savings Chart
const CumulativeTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const withoutLemma = payload.find((p: any) => p.name === 'Without Lemma')?.value || 0;
    const withLemma = payload.find((p: any) => p.name === 'With Lemma')?.value || 0;
    const netSavings = withoutLemma - withLemma;

    return (
      <div className="bg-slate-900/90 border border-white/10 rounded-xl shadow-2xl px-3 py-2.5 text-xs backdrop-blur-md">
        <p className="font-bold text-slate-400 mb-1">{label}</p>
        <p className="text-indigo-400 font-semibold flex justify-between gap-4">
          <span>Without Lemma (Direct AI):</span>
          <span>${withoutLemma.toFixed(4)}</span>
        </p>
        <p className="text-emerald-400 font-semibold flex justify-between gap-4">
          <span>With Lemma (Actual Cost):</span>
          <span>${withLemma.toFixed(4)}</span>
        </p>
        <div className="border-t border-white/5 my-1.5" />
        <p className="text-cyan-400 font-bold flex justify-between gap-4">
          <span>Cumulative Net Savings:</span>
          <span>${netSavings.toFixed(4)}</span>
        </p>
      </div>
    );
  }
  return null;
};

// Section header for charts
const ChartSection = ({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <div className="bg-slate-900/40 p-5 rounded-2xl border border-white/5 backdrop-blur-md shadow-sm hover:border-white/10 transition-all duration-300">
    <div className="flex items-start gap-3 mb-4">
      <div className="flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

// Friendly empty state for charts
const EmptyChart = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center h-48 text-center px-4">
    <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center mb-3 border border-white/5">
      <TrendingUp className="w-5 h-5 text-slate-700" />
    </div>
    <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest leading-relaxed max-w-[160px]">{message}</p>
  </div>
);

export const Charts: React.FC = () => {
  const events = useDashboardStore((state) => state.events);

  const costData = useMemo(() => {
    const grouped = new Map<string, number>();
    events.forEach((event) => {
      if (event.type !== 'cache_hit') return;
      const timestamp = new Date(event.timestamp);
      const hourKey = timestamp.toLocaleTimeString('en-US', { hour: '2-digit', hour12: false });
      grouped.set(hourKey, (grouped.get(hourKey) || 0) + (event.costSaved || 0));
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-24)
      .map(([time, cost]) => ({ time, cost }));
  }, [events]);

  const cumulativeCostData = useMemo(() => {
    const sortedEvents = [...events]
      .filter((e) => e.type === 'cache_hit' || e.type === 'cache_miss')
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    let cumulativeWithoutLemma = 0;
    let cumulativeWithLemma = 0;

    const dataPoints = sortedEvents.map((event) => {
      const isHit = event.type === 'cache_hit';
      // Use exact tokensSaved from the telemetry event payload mapped to our $0.000008 blended rate standard
      const tokensCount = event.tokensSaved || (isHit ? 2000 : 250);
      const costAmount = tokensCount * 0.000008;

      if (isHit) {
        cumulativeWithoutLemma += costAmount;
      } else {
        cumulativeWithoutLemma += costAmount;
        cumulativeWithLemma += costAmount;
      }

      const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

      return {
        time,
        'Without Lemma': parseFloat(cumulativeWithoutLemma.toFixed(4)),
        'With Lemma': parseFloat(cumulativeWithLemma.toFixed(4)),
      };
    });

    return dataPoints.slice(-20);
  }, [events]);

  const total = events.filter((e) => e.type === 'cache_hit' || e.type === 'cache_miss').length;

  return (
    <div className="grid grid-cols-1 gap-4">

      {/* Cumulative Spend Chart (ROI Visualizer) */}
      <ChartSection
        icon={
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-sm">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
        }
        title="Cumulative Spend Savings (ROI)"
        description="Visualizing the active client-memory event savings gap (Recent Buffer)"
      >
        {total === 0 ? (
          <EmptyChart message="Process some requests to see the cumulative spend analysis here." />
        ) : (
          <>
            {/* Legend summary above chart */}
            <div className="flex items-center gap-4 mb-3 px-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-indigo-400 flex-shrink-0" />
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">
                  Without Lemma (Direct AI Cost)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald-500 flex-shrink-0" />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                  With Lemma (Actual Cost)
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={cumulativeCostData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 9, fill: '#475569' }}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: '#9ca3af' }}
                  tickFormatter={(v) => `$${v.toFixed(3)}`}
                />
                <Tooltip content={<CumulativeTooltip />} />
                <Line
                  type="monotone"
                  dataKey="Without Lemma"
                  stroke="#818cf8"
                  strokeWidth={2}
                  dot={false}
                  name="Without Lemma"
                />
                <Line
                  type="monotone"
                  dataKey="With Lemma"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={false}
                  name="With Lemma"
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-slate-600 -mt-1">
              Active optimization is expanding the cost-efficiency gap
            </p>
          </>
        )}
      </ChartSection>

      {/* Cost Savings Line Chart */}
      <ChartSection
        icon={
          <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
        }
        title="Savings Over Time"
        description="How much money was saved each hour by using cached answers instead of calling the AI"
      >
        {costData.length === 0 ? (
          <EmptyChart message="Cost savings will appear here once cache hits start generating savings." />
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 px-1">
              <Info className="w-3 h-3 text-slate-600 flex-shrink-0" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Each point shows dollars saved per hour. Trending up = system working well.</p>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={costData}>
                <defs>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#475569' }}
                  label={{ value: 'Hour of day', position: 'insideBottom', offset: -5, style: { fontSize: 10, fill: '#475569', fontWeight: 'bold', textTransform: 'uppercase' } }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  tickFormatter={(v) => `$${v.toFixed(2)}`}
                />
                <Tooltip content={<LineTooltip />} />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="url(#costGradient)"
                  strokeWidth={2.5}
                  dot={{ fill: '#8b5cf6', r: 3 }}
                  activeDot={{ r: 5, fill: '#7c3aed' }}
                  name="Savings per hour ($)"
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </ChartSection>
    </div>
  );
};
